import { Injectable } from '@angular/core';

export interface SelfAssessmentState {
  selectedCourse: any;
  selectAssessment: any;
  selectedStatus: string;
  selectedNum: number;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class SelfAssessmentStateService {
  private readonly STORAGE_KEY = 'selfAssessmentState';
  private readonly STATE_EXPIRY_HOURS = 24; // State expires after 24 hours

  constructor() { }

  /**
   * Save the current component state to localStorage
   */
  saveState(state: Partial<SelfAssessmentState>): void {
    try {
      const currentState = this.getState();
      const updatedState: SelfAssessmentState = {
        ...currentState,
        ...state,
        timestamp: new Date().getTime()
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedState));
      console.log('Self-assessment state saved:', updatedState);
    } catch (error) {
      console.error('Error saving self-assessment state:', error);
    }
  }

  /**
   * Restore the component state from localStorage
   */
  getState(): SelfAssessmentState | null {
    try {
      const savedState = localStorage.getItem(this.STORAGE_KEY);
      if (!savedState) {
        return null;
      }

      const state: SelfAssessmentState = JSON.parse(savedState);
      
      // Check if state has expired
      if (this.isStateExpired(state.timestamp)) {
        this.clearState();
        return null;
      }

      console.log('Self-assessment state restored:', state);
      return state;
    } catch (error) {
      console.error('Error restoring self-assessment state:', error);
      return null;
    }
  }

  /**
   * Clear the saved state
   */
  clearState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('Self-assessment state cleared');
    } catch (error) {
      console.error('Error clearing self-assessment state:', error);
    }
  }

  /**
   * Check if the state has expired
   */
  private isStateExpired(timestamp: number): boolean {
    const now = new Date().getTime();
    const expiryTime = this.STATE_EXPIRY_HOURS * 60 * 60 * 1000; // Convert hours to milliseconds
    return (now - timestamp) > expiryTime;
  }

  /**
   * Update only the selected course
   */
  updateSelectedCourse(courseId: any): void {
    this.saveState({ selectedCourse: courseId });
  }

  /**
   * Update only the selected assessment
   */
  updateSelectedAssessment(assessmentId: any): void {
    this.saveState({ selectAssessment: assessmentId });
  }

  /**
   * Update only the selected status filter
   */
  updateSelectedStatus(status: string): void {
    this.saveState({ selectedStatus: status });
  }

  /**
   * Update only the selected number of rows
   */
  updateSelectedNum(num: number): void {
    this.saveState({ selectedNum: num });
  }
} 